import React, { useState } from 'react';
import { Form, Button, Alert, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { dashboardAPI } from '../../../services/api';
import * as Yup from 'yup';
import { useFormik } from 'formik';

interface ChangePasswordProps {
  show: boolean;
  onHide: () => void;
  onSuccess?: () => void;
}

const ChangePassword: React.FC<ChangePasswordProps> = ({ show, onHide, onSuccess }) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  interface PasswordFormValues {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
  }

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    } as PasswordFormValues,
    validationSchema: Yup.object({
      oldPassword: Yup.string()
        .required(t('Validation.old_password_required') || 'Current password is required'),
      newPassword: Yup.string()
        .min(8, t('Validation.password_min_length') || 'Password must be at least 8 characters')
        .required(t('Validation.new_password_required') || 'New password is required'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword')], t('Validation.passwords_must_match') || 'Passwords must match')
        .required(t('Validation.confirm_password_required') || 'Please confirm your password')
    }),
    onSubmit: async (values: PasswordFormValues) => {
      setIsSubmitting(true);
      setError(null);
      setSuccess(false);

      try {
        await dashboardAPI.changePassword(values.oldPassword, values.newPassword);
        setSuccess(true);
        
        // Reset form
        validation.resetForm();
        
        // Call success callback if provided
        if (onSuccess) {
          onSuccess();
        }
        
        // Auto-close after 2 seconds
        setTimeout(() => {
          handleClose();
        }, 2000);
      } catch (err: any) {
        console.error('Error changing password:', err);
        
        // Extract error message from response
        const responseData = err?.response?.data || {};
        const errorCode = responseData.errorCode;
        const backendMessage = responseData.message || '';
        const userMessage = responseData.userMessage || '';
        
        let errorMessage = userMessage || backendMessage || err?.message || 
                          t('Profile.password_change_error') || 
                          'Failed to change password. Please try again.';
        
        // Translate common error messages - check for password incorrect errors
        // This handles both PasswordChangeException (AUTH_002) and InvalidCredentialsException (AUTH_001)
        if (errorCode === 'AUTH_002' || 
            errorCode === 'AUTH_001' ||
            backendMessage.toLowerCase().includes('current password is incorrect') || 
            backendMessage.toLowerCase().includes('invalid username or password') ||
            userMessage.toLowerCase().includes('incorrect') ||
            errorMessage.toLowerCase().includes('current password is incorrect') || 
            errorMessage.toLowerCase().includes('invalid credentials') ||
            errorMessage.toLowerCase().includes('incorrect password')) {
          errorMessage = t('Profile.incorrect_current_password') || 'Mevcut şifre hatalı. Lütfen doğru şifreyi girin.';
        } else if (errorMessage.toLowerCase().includes('password')) {
          // Try to use the translated error message
          errorMessage = t('Profile.password_change_error') || errorMessage;
        }
        
        setError(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  const handleClose = () => {
    validation.resetForm();
    setError(null);
    setSuccess(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('Profile.change_password') || 'Change Password'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success">
            {t('Profile.password_change_success') || 'Password changed successfully!'}
          </Alert>
        )}

        <Form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <Form.Group className="mb-3" controlId="oldPassword">
            <Form.Label>{t('Profile.current_password') || 'Current Password'} <span className="text-danger">*</span></Form.Label>
            <div className="position-relative">
              <Form.Control
                type={showOldPassword ? "text" : "password"}
                name="oldPassword"
                placeholder={t('Profile.enter_current_password') || 'Enter current password'}
                value={validation.values.oldPassword}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                isInvalid={validation.touched.oldPassword && !!validation.errors.oldPassword}
                disabled={isSubmitting}
              />
              <button
                className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                type="button"
                onClick={() => setShowOldPassword(!showOldPassword)}
                style={{ border: 'none', background: 'none', padding: '8px' }}
              >
                <i className={showOldPassword ? "ri-eye-off-fill" : "ri-eye-fill"}></i>
              </button>
              {validation.touched.oldPassword && validation.errors.oldPassword && (
                <Form.Control.Feedback type="invalid">
                  {validation.errors.oldPassword}
                </Form.Control.Feedback>
              )}
            </div>
          </Form.Group>

          <Form.Group className="mb-3" controlId="newPassword">
            <Form.Label>{t('Profile.new_password') || 'New Password'} <span className="text-danger">*</span></Form.Label>
            <div className="position-relative">
              <Form.Control
                type={showNewPassword ? "text" : "password"}
                name="newPassword"
                placeholder={t('Profile.enter_new_password') || 'Enter new password (min. 8 characters)'}
                value={validation.values.newPassword}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                isInvalid={validation.touched.newPassword && !!validation.errors.newPassword}
                disabled={isSubmitting}
              />
              <button
                className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                style={{ border: 'none', background: 'none', padding: '8px' }}
              >
                <i className={showNewPassword ? "ri-eye-off-fill" : "ri-eye-fill"}></i>
              </button>
              {validation.touched.newPassword && validation.errors.newPassword && (
                <Form.Control.Feedback type="invalid">
                  {validation.errors.newPassword}
                </Form.Control.Feedback>
              )}
            </div>
            <Form.Text className="text-muted">
              {t('Profile.password_requirements') || 'Password must be at least 8 characters long'}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="confirmPassword">
            <Form.Label>{t('Profile.confirm_password') || 'Confirm New Password'} <span className="text-danger">*</span></Form.Label>
            <div className="position-relative">
              <Form.Control
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                placeholder={t('Profile.confirm_new_password') || 'Confirm new password'}
                value={validation.values.confirmPassword}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                isInvalid={validation.touched.confirmPassword && !!validation.errors.confirmPassword}
                disabled={isSubmitting}
              />
              <button
                className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{ border: 'none', background: 'none', padding: '8px' }}
              >
                <i className={showConfirmPassword ? "ri-eye-off-fill" : "ri-eye-fill"}></i>
              </button>
              {validation.touched.confirmPassword && validation.errors.confirmPassword && (
                <Form.Control.Feedback type="invalid">
                  {validation.errors.confirmPassword}
                </Form.Control.Feedback>
              )}
            </div>
          </Form.Group>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
              {t('Common.cancel') || 'Cancel'}
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {t('Profile.changing') || 'Changing...'}
                </>
              ) : (
                t('Profile.change_password') || 'Change Password'
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ChangePassword;

